import { inject, injectable, interfaces } from 'inversify';
import { v4 } from 'uuid';
import {
  IRequestClarificationDuringAnnotationRequest,
  IRequestClarificationDuringFreeAccessEditRequest,
  IRequestClarificationDuringReviewEditRequest,
  IRequestClarificationDuringReviewRequest,
} from '../../../../../../modules/editor/models/Requests';
import { IBatchAnnotationApiService } from '../../../../../../modules/editor/services/BatchAnnotationApi.service';
import { IFreeAccessAnnotationApiService } from '../../../../../../modules/editor/services/FreeAccessAnnotationApi.service';
import { ACDLError } from '../../../../../models/error.model';
import { SortingDirection } from '../../../../../models/sortingDirection.model';
import { IEventBus } from '../../../../../services/eventBus.service';
import { ITimerService } from '../../../../../services/timer.service';
import { IAnnotationDto } from '../../../annotations.interface';
import { IAnnotationsStore } from '../../../annotations.store';
import { ImageScopeName } from '../../../question.model';
import { IUndoRedoHistory } from '../../../undoRedoHistory.service';
import { IClarificationsStore } from '../Clarifications.store';
import { ClarificationAddedEvent } from '../events/ClarificationAddedEvent';
import { ClarifyRequest, GetClarificationsRequest, IRequestClarificationRequest } from '../models/Clarifications.requests';
import { IClarificationsApiService } from './ClarificationsApi.service';

export interface IClarificationsBl {
  getClarificationsAsync(projectId: string, imageId: string): Promise<void>;
  batchRequestClarificationDuringAnnotationAsync(): Promise<void>;
  batchRequestClarificationDuringReviewAsync(): Promise<void>;
  batchRequestClarificationDuringReviewEditAsync(): Promise<void>;
  freeAccessRequestClarificationDuringAnnotationAsync(): Promise<void>;
  freeAccessRequestClarificationDuringEditAsync(): Promise<void>;
  freeAccessRequestClarificationDuringReviewAsync(): Promise<void>;
  freeAccessRequestClarificationDuringReviewEditAsync(): Promise<void>;
  clarifyAsync(projectId: string, clarificationId: string, answer: string): Promise<void>;
  setClarificationsListSortDirection(sortDirection: SortingDirection): void;
  setClarificationQuestion(question: string): void;
  dispose(): void;
}

export namespace IClarificationsBl {
  export const $: interfaces.ServiceIdentifier<IClarificationsBl> = Symbol('CLARIFICATIONS_BL');
}

@injectable()
export class ClarificationsBl implements IClarificationsBl {
  constructor(
    @inject(IAnnotationsStore.$) private readonly annotationsStore: IAnnotationsStore,
    @inject(IClarificationsStore.$) private readonly clarificationsStore: IClarificationsStore,
    @inject(IClarificationsStore.$) private readonly clarificationsStoreSetter: IClarificationsStore,
    @inject(IEventBus.$) private readonly eventBus: IEventBus,
    @inject(ITimerService.$) public readonly timer: ITimerService,
    @inject(IBatchAnnotationApiService.$) private readonly batchAnnotationApiService: IBatchAnnotationApiService,
    @inject(IFreeAccessAnnotationApiService.$) private readonly freeAccessAnnotationApiService: IFreeAccessAnnotationApiService,
    @inject(IClarificationsApiService.$) private readonly clarificationsApiService: IClarificationsApiService,
    @inject(IUndoRedoHistory.$) private readonly undoRedoHistory: IUndoRedoHistory
  ) {}

  async getClarificationsAsync(projectId: string, imageId: string): Promise<void> {
    const request = new GetClarificationsRequest(projectId, imageId);
    const result = await this.clarificationsApiService.getClarificationsAsync(request);

    if (result instanceof ACDLError) {
      throw result;
    }

    this.clarificationsStoreSetter.setArchiveClarifications(result);
  }

  async batchRequestClarificationDuringAnnotationAsync(): Promise<void> {
    if (!this.annotationsStore.id && !this.undoRedoHistory.canUndo) {
      await this.clarificationsApiService.requestClarificationAsync(this.createRequestClarificationRequest());
    } else {
      await this.batchAnnotationApiService.requestClarificationDuringAnnotationAsync(this.createRequestClarificationDuringAnnotationRequest());
    }

    this.eventBus.sendEvent(new ClarificationAddedEvent());
  }

  async batchRequestClarificationDuringReviewAsync(): Promise<void> {
    await this.batchAnnotationApiService.requestClarificationDuringReviewAsync(this.createRequestClarificationDuringReviewRequest());
    this.eventBus.sendEvent(new ClarificationAddedEvent());
  }

  async batchRequestClarificationDuringReviewEditAsync(): Promise<void> {
    await this.batchAnnotationApiService.requestClarificationDuringReviewEditAsync(this.createRequestClarificationDuringReviewEditRequest());
    this.eventBus.sendEvent(new ClarificationAddedEvent());
  }

  async freeAccessRequestClarificationDuringAnnotationAsync(): Promise<void> {
    if (!this.annotationsStore.id && !this.undoRedoHistory.canUndo) {
      await this.clarificationsApiService.requestClarificationAsync(this.createRequestClarificationRequest());
    } else {
      await this.freeAccessAnnotationApiService.requestClarificationDuringAnnotationAsync(this.createRequestClarificationDuringAnnotationRequest());
    }

    this.eventBus.sendEvent(new ClarificationAddedEvent());
  }

  async freeAccessRequestClarificationDuringReviewAsync(): Promise<void> {
    await this.freeAccessAnnotationApiService.requestClarificationDuringReviewAsync(this.createRequestClarificationDuringReviewRequest());
    this.eventBus.sendEvent(new ClarificationAddedEvent());
  }

  async freeAccessRequestClarificationDuringReviewEditAsync(): Promise<void> {
    await this.freeAccessAnnotationApiService.requestClarificationDuringReviewEditAsync(this.createRequestClarificationDuringReviewEditRequest());
    this.eventBus.sendEvent(new ClarificationAddedEvent());
  }

  async freeAccessRequestClarificationDuringEditAsync(): Promise<void> {
    await this.freeAccessAnnotationApiService.requestClarificationDuringEditAsync(this.createRequestClarificationDuringFreeAccessEditRequest());
    this.eventBus.sendEvent(new ClarificationAddedEvent());
  }

  async clarifyAsync(projectId: string, clarificationId: string, answer: string): Promise<void> {
    if (this.annotationsStore.image) {
      const request = new ClarifyRequest(clarificationId, answer);
      await this.clarificationsApiService.clarifyAsync(request);
      await this.getClarificationsAsync(projectId, this.annotationsStore.image.id);
    }
  }

  setClarificationQuestion(question: string): void {
    this.clarificationsStoreSetter.setQuestion(question);
  }

  setClarificationsListSortDirection(sortDirection: SortingDirection): void {
    this.clarificationsStoreSetter.setClarificationsListSortDirection(sortDirection);
  }

  dispose(): void {
    this.clarificationsStoreSetter.clear();
  }

  private createRequestClarificationDuringAnnotationRequest(): IRequestClarificationDuringAnnotationRequest {
    if (!this.annotationsStore.image) {
      throw 'Image can not be empty';
    }

    const id = this.annotationsStore.id || v4();
    return {
      id,
      projectId: this.annotationsStore.projectId,
      imageId: this.annotationsStore.image.id,
      duration: this.timer.duration,
      annotation: {
        id,
        questions: this.annotationsStore.questions
          .filter((q) => q.scopes.includes(ImageScopeName))
          .map((q) => ({ id: q.id, answer: q.answer, answers: q.answers.map((a) => ({ id: a.id, selected: a.selected })) })),
        segmentations: this.annotationsStore.segmentations.map((s) => ({
          id: s.id,
          questions: s.questions,
          feature: {
            type: s.feature.type,
            properties: s.feature.properties,
            geometry: s.feature.geometry,
          },
          priority: s.priority,
        })),
      } as IAnnotationDto,
      question: this.clarificationsStore.question,
    };
  }

  private createRequestClarificationRequest(): IRequestClarificationRequest {
    if (!this.annotationsStore.image) {
      throw 'Image can not be empty';
    }

    return {
      projectId: this.annotationsStore.projectId,
      imageId: this.annotationsStore.image.id,
      question: this.clarificationsStore.question,
      duration: this.timer.duration,
    };
  }

  private createRequestClarificationDuringReviewRequest(): IRequestClarificationDuringReviewRequest {
    if (!this.annotationsStore.image) {
      throw 'Image can not be empty';
    }

    const id = this.annotationsStore.id || v4();
    return {
      id,
      projectId: this.annotationsStore.projectId,
      imageId: this.annotationsStore.image.id,
      duration: this.timer.duration,
      question: this.clarificationsStore.question,
    };
  }

  private createRequestClarificationDuringReviewEditRequest(): IRequestClarificationDuringReviewEditRequest {
    if (!this.annotationsStore.image) {
      throw 'Image can not be empty';
    }

    const id = this.annotationsStore.id || v4();
    return {
      id,
      projectId: this.annotationsStore.projectId,
      imageId: this.annotationsStore.image.id,
      duration: this.timer.duration,
      annotation: {
        id,
        questions: this.annotationsStore.questions
          .filter((q) => q.scopes.includes(ImageScopeName))
          .map((q) => ({ id: q.id, answer: q.answer, answers: q.answers.map((a) => ({ id: a.id, selected: a.selected })) })),
        segmentations: this.annotationsStore.segmentations.map((s) => ({
          id: s.id,
          questions: s.questions,
          feature: {
            type: s.feature.type,
            properties: s.feature.properties,
            geometry: s.feature.geometry,
          },
          priority: s.priority,
        })),
      } as IAnnotationDto,
      question: this.clarificationsStore.question,
    };
  }

  private createRequestClarificationDuringFreeAccessEditRequest(): IRequestClarificationDuringFreeAccessEditRequest {
    if (!this.annotationsStore.image) {
      throw 'Image can not be empty';
    }

    const id = this.annotationsStore.id || v4();
    return {
      id,
      projectId: this.annotationsStore.projectId,
      imageId: this.annotationsStore.image.id,
      duration: this.timer.duration,
      annotation: {
        id,
        questions: this.annotationsStore.questions
          .filter((q) => q.scopes.includes(ImageScopeName))
          .map((q) => ({ id: q.id, answer: q.answer, answers: q.answers.map((a) => ({ id: a.id, selected: a.selected })) })),
        segmentations: this.annotationsStore.segmentations.map((s) => ({
          id: s.id,
          questions: s.questions,
          feature: {
            type: s.feature.type,
            properties: s.feature.properties,
            geometry: s.feature.geometry,
          },
          priority: s.priority,
        })),
      } as IAnnotationDto,
      question: this.clarificationsStore.question,
    };
  }
}
